import { useEffect, useRef, useState } from "react";
import { DataNotFound } from "../common/DataNotFound";
import { Building2, Check, ChevronDown, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { organisationApi } from "../../api/organisationApi";
import { useOrganisationSwitch } from "../../auth/OrganisationSwitchProvider";
import { ConfirmModal } from "../common/ConfirmModal";

interface OrgOption {
  id: string;
  name: string;
}

export function OrganisationSwitcher() {
  const { t } = useTranslation(["topbar", "common"]);
  const { currentOrganisation, switching, switchOrganisation } =
    useOrganisationSwitch();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingOrg, setPendingOrg] = useState<OrgOption | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await organisationApi.searchOrganisations({
          pageSize: 20,
          pageIndex: 0,
          searchText: search,
          isActive: true,
        });
        if (cancelled) return;

        const data = res?.data || [];
        setOptions(
          data.map((org: { id: string; title?: string; name?: string }) => ({
            id: org.id,
            name: org.title || org.name || t("unknownOrganisation"),
          })),
        );
      } catch (err) {
        console.error("Failed to load organisations:", err);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, search]);

  const handleSelect = (org: OrgOption) => {
    setIsOpen(false);
    if (org.id === currentOrganisation?.id) return;
    setPendingOrg(org);
  };

  const handleConfirmSwitch = async () => {
    if (!pendingOrg) return;
    await switchOrganisation(pendingOrg.id, pendingOrg.name);
    setPendingOrg(null);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        disabled={switching}
        title={t("switchOrganisationLabel", {
          defaultValue: "Switch Organisation",
        })}
        aria-label={`${t("switchOrganisationLabel", { defaultValue: "Switch Organisation" })}: ${currentOrganisation?.name ?? ""}`}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border border-[rgb(var(--border))] bg-[rgb(var(--card))] text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--card-muted))] transition disabled:opacity-60 h-9 sm:h-auto max-w-[110px] sm:max-w-[220px] md:max-w-[320px] shrink-0"
      >
        <Building2
          size={13}
          className="text-[rgb(var(--text-accent))] shrink-0"
        />
        <span className="hidden font-semibold truncate sm:inline">
          {switching
            ? t("switchingOrganisation", { defaultValue: "Switching..." })
            : currentOrganisation?.name ||
              t("selectOrganisation", { defaultValue: "Select Organisation" })}
        </span>
        <ChevronDown
          size={12}
          className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Phones: the trigger is icon-only near the middle of the bar, so a
          right-aligned 18rem panel ran off the left edge. There it becomes a
          full-width sheet under the topbar; from sm it is the usual dropdown.
          (The header's backdrop-blur makes it the containing block for
          `fixed`, so top/inset are measured from the header itself.) */}
      {isOpen && (
        <div className="fixed inset-x-3 top-[3.75rem] z-50 bg-[rgb(var(--card))] border border-[rgb(var(--border))] rounded-lg shadow-lg overflow-hidden sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-1 sm:w-72">
          <div className="px-3 py-2 border-b border-[rgb(var(--border))] bg-[rgb(var(--card-muted))]">
            <span className="text-xs font-bold text-[rgb(var(--text-primary))]">
              {t("switchOrganisationLabel", {
                defaultValue: "Switch Organisation",
              })}
            </span>
          </div>

          <div className="p-2 border-b border-[rgb(var(--border))]">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-[rgb(var(--bg))] border border-[rgb(var(--border))]">
              <Search size={13} className="text-[rgb(var(--text-muted))]" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("searchOrganisation", {
                  defaultValue: "Search organisation...",
                })}
                className="flex-1 bg-transparent text-xs outline-none text-[rgb(var(--text-primary))]"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-4 text-xs text-center text-[rgb(var(--text-muted))]">
                {t("loading", { ns: "common", defaultValue: "Loading..." })}
              </div>
            ) : options.length === 0 ? (
              <DataNotFound
                inline
                title={t("noOrganisationsFound", {
                  defaultValue: "No organisations found",
                })}
              />
            ) : (
              options.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => handleSelect(org)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left hover:bg-[rgb(var(--card-muted))] transition"
                >
                  <span className="truncate text-[rgb(var(--text-primary))]">
                    {org.name}
                  </span>
                  {org.id === currentOrganisation?.id && (
                    <Check
                      size={14}
                      className="text-[rgb(var(--text-accent))] shrink-0"
                    />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingOrg}
        title={t("switchOrganisationLabel", {
          defaultValue: "Switch Organisation",
        })}
        description={t("switchOrganisationConfirm", {
          name: pendingOrg?.name,
          defaultValue: `Switch to "${pendingOrg?.name}"? You'll now be working within this organisation's data.`,
        })}
        confirmLabel={t("switchOrganisationLabel", {
          defaultValue: "Switch Organisation",
        })}
        variant="warning"
        onConfirm={handleConfirmSwitch}
        onCancel={() => setPendingOrg(null)}
      />
    </div>
  );
}
